/**
 * PATH: apps/hr/src/pages/employees/employee-detail/employee-api-types.ts
 * PURPOSE: API response types and mapping functions for employee detail
 */

import type { Employee, EmploymentType, Document } from '../../../types'

export interface WorkerDetailApiResponse {
  id: string
  first_name: string
  last_name: string
  full_name: string
  email: string | null
  mobile: string | null
  role: string
  trade: string | null
  employment_type: string
  is_active: boolean
  simpro_id: number | null
  pay_rate?: number
  hourly_rate?: number
  created_at?: string
  updated_at?: string
  department?: string | null
  position?: string | null
  hire_date?: string | null
  termination_date?: string | null
  manager_id?: string | null
  manager_name?: string | null
  location?: string | null
  address_line_1?: string | null
  address_line_2?: string | null
  city?: string | null
  state?: string | null
  postcode?: string | null
  country?: string | null
  date_of_birth?: string | null
  gender?: string | null
}

const ROLE_TO_POSITION: Record<string, string> = {
  installer: 'Installer', supervisor: 'Supervisor', leading_hand: 'Leading Hand',
  labourer: 'Labourer', pm: 'Project Manager', admin: 'Administrator',
  scheduler: 'Scheduler', apprentice: 'Apprentice',
}

const EMPLOYMENT_TYPE_MAP: Record<string, EmploymentType> = {
  ft: 'full_time', pt: 'part_time', casual: 'casual', subcontractor: 'contractor',
}

export function mapWorkerDetailToEmployee(worker: WorkerDetailApiResponse): Employee {
  return {
    id: worker.id,
    employeeId: worker.simpro_id ? `SP${worker.simpro_id}` : worker.id.slice(0, 8).toUpperCase(),
    firstName: worker.first_name,
    lastName: worker.last_name,
    email: worker.email || '',
    phone: worker.mobile || '',
    department: worker.department || worker.trade || 'General',
    position: worker.position || ROLE_TO_POSITION[worker.role] || worker.role || 'Team Member',
    employmentType: EMPLOYMENT_TYPE_MAP[worker.employment_type] || 'full_time',
    status: worker.is_active ? 'active' : 'inactive',
    hireDate: worker.hire_date || worker.created_at?.split('T')[0] || '',
    terminationDate: worker.termination_date || undefined,
    managerId: worker.manager_id || undefined,
    managerName: worker.manager_name || undefined,
    location: worker.location || undefined,
    address: (worker.address_line_1 || worker.city || worker.state || worker.postcode) ? {
      line1: worker.address_line_1 || '',
      line2: worker.address_line_2 || undefined,
      city: worker.city || '',
      state: worker.state || '',
      postalCode: worker.postcode || '',
      country: worker.country || 'Australia',
    } : undefined,
    payRate: worker.pay_rate || worker.hourly_rate ? {
      hourlyRate: worker.hourly_rate || worker.pay_rate || 0,
      overtimeMultiplier: 1,
      superRate: 0,
    } : undefined,
    createdAt: worker.created_at || '',
    updatedAt: worker.updated_at || '',
    dateOfBirth: worker.date_of_birth || undefined,
    gender: worker.gender || undefined,
    role: worker.role,
    trade: worker.trade || undefined,
  }
}

export function mapTicketTypeToDocumentType(ticketType: string): Document['documentType'] {
  const typeMap: Record<string, Document['documentType']> = {
    'Driving License': 'license', 'White Card': 'license', 'EWP License': 'license',
    'Forklift License': 'license', 'First Aid Certificate': 'certification',
    'Certificate': 'certification', 'Passport': 'identification',
    'Working With Children Check': 'certification', 'Police Check': 'certification',
    'Trade Certificate': 'certification', 'Visa': 'visa', 'Medical': 'medical', 'Training': 'training',
  }
  return typeMap[ticketType] || 'other'
}

export const EMPLOYMENT_TYPE_TO_API: Record<string, string> = {
  full_time: 'ft', part_time: 'pt', casual: 'casual', contractor: 'subcontractor',
}
